/**
 * Store reactivo para manejo de tickets sin WebSocket (webhook mode)
 * Portal de Soporte Ecomac - Webhook-based Tickets Store
 */

import { computed, reactive, nextTick, readonly } from 'vue';
// import { useWebSocket } from '../services/WebSocketService'; // DESACTIVADO: WebSocket no disponible por ahora
import { useTicketStatusService } from '../services/TicketStatusService';
import { useNotificationService } from '../services/NotificationService';
import TicketService from '../services/TicketService';

export interface Ticket {
  id: string;
  title: string;
  description: string;
  status: 'abierto' | 'en_progreso' | 'resuelto' | 'cerrado';
  priority: 'alta' | 'media' | 'baja';
  source: string; // Cambiado de 'elias-ai' | 'escalado' a string para compatibilidad
  createdAt: string;
  updatedAt: string;
  assignedTo: string;
  assignedToAvatar?: string | null;
  assignedToData?: any | null;
  assignedToEmail: string;
  userEmail: string;
  requesterName: string;
  escalated: boolean;
  resolved: boolean;
  solution: string;
  tags: string[];
  attachments: any[];
  conversationHistory: any[];
  custom_fields: any[];
  checklists: any[];
  assignees: any[];
  watchers: any[];
  progressPercent?: number;
  progressSource?: string;
  clickupUrl: string;
  _raw: any;
  clickup?: {
    custom_id: string;
    orderindex: string;
    list: string;
    project: string;
    folder: string;
    due_date?: string;
    time_estimate?: number;
  };
  // Campos adicionales para funcionalidades locales
  isFavorite?: boolean;
  lastActivity?: string;
  slaPercent?: number;
}

export interface TicketsState {
  tickets: Map<string, Ticket>;
  loading: boolean;
  error: string | null;
  filters: {
    status: string[];
    priority: string[];
    assignedTo: string[];
    search: string;
    escalated: boolean | null;
    favorites: boolean;
  };
  sortBy: 'createdAt' | 'updatedAt' | 'priority' | 'status';
  sortOrder: 'asc' | 'desc';
  lastSync: string | null;
}

/**
 * Store principal de tickets sin WebSocket (usando webhook POST)
 */
export function useTicketsStore() {
  // Servicios
  // const webSocket = useWebSocket(); // DESACTIVADO: WebSocket no disponible por ahora
  const statusService = useTicketStatusService();
  const notifications = useNotificationService();
  
  // Flag para indicar que WebSocket está desactivado
  const isWebSocketEnabled = false;
  
  // Estado del store
  const state = reactive<TicketsState>({
    tickets: new Map(),
    loading: false,
    error: null,
    filters: {
      status: [],
      priority: [],
      assignedTo: [],
      search: '',
      escalated: null,
      favorites: false
    },
    sortBy: 'createdAt',
    sortOrder: 'desc',
    lastSync: null
  });
  
  // Computed properties
  const tickets = computed(() => Array.from(state.tickets.values()));
  
  const filteredTickets = computed(() => {
    let filtered = tickets.value;
    
    // Filtro por búsqueda de texto
    if (state.filters.search.trim()) {
      const searchTerm = state.filters.search.toLowerCase();
      filtered = filtered.filter(ticket =>
        ticket.title.toLowerCase().includes(searchTerm) ||
        ticket.description.toLowerCase().includes(searchTerm) ||
        ticket.id.toLowerCase().includes(searchTerm) ||
        ticket.requesterName.toLowerCase().includes(searchTerm)
      );
    }
    
    // Filtro por estado
    if (state.filters.status.length > 0) {
      filtered = filtered.filter(ticket =>
        state.filters.status.includes(ticket.status)
      );
    }
    
    // Filtro por prioridad
    if (state.filters.priority.length > 0) {
      filtered = filtered.filter(ticket =>
        state.filters.priority.includes(ticket.priority)
      );
    }
    
    // Filtro por asignado a
    if (state.filters.assignedTo.length > 0) {
      filtered = filtered.filter(ticket =>
        state.filters.assignedTo.includes(ticket.assignedTo)
      );
    }
    
    // Filtro por escalado
    if (state.filters.escalated !== null) {
      filtered = filtered.filter(ticket =>
        ticket.escalated === state.filters.escalated
      );
    }
    
    // Filtro por favoritos
    if (state.filters.favorites) {
      filtered = filtered.filter(ticket => ticket.isFavorite === true);
    }
    
    // Ordenamiento
    filtered.sort((a, b) => {
      let valueA: any, valueB: any;
      
      switch (state.sortBy) {
        case 'createdAt':
          valueA = new Date(a.createdAt).getTime();
          valueB = new Date(b.createdAt).getTime();
          break;
        case 'updatedAt':
          valueA = new Date(a.updatedAt).getTime();
          valueB = new Date(b.updatedAt).getTime();
          break;
        case 'priority':
          const priorityOrder = { 'alta': 3, 'media': 2, 'baja': 1 };
          valueA = priorityOrder[a.priority] || 0;
          valueB = priorityOrder[b.priority] || 0;
          break;
        case 'status':
          const statusA = statusService.getStatusData(a.status);
          const statusB = statusService.getStatusData(b.status);
          valueA = statusA?.order || 0;
          valueB = statusB?.order || 0;
          break;
        default:
          return 0;
      }
      
      if (state.sortOrder === 'asc') {
        return valueA - valueB;
      } else {
        return valueB - valueA;
      }
    });
    
    return filtered;
  });
  
  const ticketsByStatus = computed(() => {
    const grouped: Record<string, Ticket[]> = {};
    
    tickets.value.forEach(ticket => {
      if (!grouped[ticket.status]) {
        grouped[ticket.status] = [];
      }
      grouped[ticket.status].push(ticket);
    });
    
    return grouped;
  });
  
  const statusStatistics = computed(() => {
    return statusService.getStatusStatistics(tickets.value);
  });
  
  // Acciones del store
  const actions = {
    /**
     * Inicializar el store (modo webhook sin WebSocket)
     */
    async initialize(): Promise<void> {
      try {
        state.loading = true;
        state.error = null;
        // DESACTIVADO: No conectar WebSocket ya que no está disponible
        // await webSocket.connect();
        // setupWebSocketListeners();
        
        // Cargar datos iniciales usando webhook (SIN notificaciones)
        await this.loadTickets(undefined, { showNotifications: false });
        
        console.log('✅ Store inicializado correctamente (modo webhook)');
        
      } catch (error) {
        state.error = `Error al inicializar: ${error}`;
        notifications.error('Error al inicializar el sistema de tickets');
        console.error('❌ Error inicializando store:', error);
      } finally {
        state.loading = false;
      }
    },
    
    /**
     * Cargar tickets usando TicketService (método recomendado)
     */
    async loadTickets(userEmail?: string, options?: { showNotifications?: boolean }): Promise<void> {
      try {
        state.loading = true;
        state.error = null;
        // Usar TicketService que tiene toda la lógica de normalización
        const tickets = await TicketService.fetchTickets(userEmail || 'cliente@ecomac.cl');
        
        
        // Limpiar tickets existentes
        state.tickets.clear();
        
        // Agregar tickets nuevos usando el método del store
        if (tickets && tickets.length > 0) {
          tickets.forEach((ticket: Ticket) => {
            this.addOrUpdateTicket(ticket);
          });
          
          state.lastSync = new Date().toISOString();
          
          // IMPORTANTE: Solo mostrar notificación si se solicita EXPLÍCITAMENTE
          if (options?.showNotifications === true) {
            notifications.success(`${tickets.length} tickets cargados`);
          }
        } else {
          // Solo mostrar "no se encontraron" si es una carga manual
          if (options?.showNotifications === true) {
            notifications.info('No se encontraron tickets');
          }
        }
        
      } catch (error) {
        console.error('❌ STORE - Error al cargar tickets:', error);
        state.error = `Error al cargar tickets: ${error}`;
        
        // Los errores siempre se notifican
        notifications.error('Error al cargar tickets');
      } finally {
        state.loading = false;
      }
    },
    
    /**
     * Agregar o actualizar ticket en el store
     */
    addOrUpdateTicket(ticket: Ticket): void {
      const existingTicket = state.tickets.get(ticket.id);
      
      // Preservar propiedades locales si existe el ticket
      if (existingTicket) {
        ticket.isFavorite = existingTicket.isFavorite;
        ticket.lastActivity = ticket.updatedAt;
      }
      
      state.tickets.set(ticket.id, ticket);
    },
    
    /**
     * Eliminar ticket del store
     */
    removeTicket(ticketId: string): void {
      state.tickets.delete(ticketId);
    },
    
    /**
     * Obtener ticket por ID
     */
    getTicket(ticketId: string): Ticket | undefined {
      return state.tickets.get(ticketId);
    },
    
    /**
     * Actualizar ticket completo
     */
    updateTicket(updatedTicket: Ticket): void {
      const existingTicket = state.tickets.get(updatedTicket.id);
      
      if (existingTicket) {
        // Preservar propiedades locales si existen
        updatedTicket.isFavorite = existingTicket.isFavorite;
        updatedTicket.lastActivity = new Date().toISOString();
        
        // Actualizar el ticket en el store
        state.tickets.set(updatedTicket.id, updatedTicket);
        
        // Notificar cambio
        notifications.success(`Ticket #${updatedTicket.id} actualizado`);
        
        // Emitir evento local para componentes que lo escuchen
        nextTick(() => {
          window.dispatchEvent(new CustomEvent('ticket-updated', {
            detail: { ticket: updatedTicket }
          }));
        });
      } else {
        console.warn(`Ticket ${updatedTicket.id} no encontrado para actualizar`);
      }
    },
    
    /**
     * Actualizar estado de un ticket
     */
    updateTicketStatus(ticketId: string, newStatus: 'abierto' | 'en_progreso' | 'resuelto' | 'cerrado'): void {
      const ticket = state.tickets.get(ticketId);
      if (ticket) {
        const oldStatus = ticket.status;
        ticket.status = newStatus;
        ticket.updatedAt = new Date().toISOString();
        ticket.lastActivity = ticket.updatedAt;
        
        // Notificar cambio de estado
        const statusData = statusService.getStatusData(newStatus);
        if (statusData) {
          notifications.info(
            `Ticket #${ticketId} cambió a: ${statusData.displayName}`
          );
        }
        
        // Emitir evento local para componentes que lo escuchen
        nextTick(() => {
          window.dispatchEvent(new CustomEvent('ticket-status-changed', {
            detail: { ticketId, oldStatus, newStatus }
          }));
        });
      }
    },
    
    /**
     * Toggle favorito de un ticket
     */
    toggleFavorite(ticketId: string): void {
      const ticket = state.tickets.get(ticketId);
      if (ticket) {
        ticket.isFavorite = !ticket.isFavorite;
        
        const message = ticket.isFavorite 
          ? 'Ticket agregado a favoritos' 
          : 'Ticket removido de favoritos';
        notifications.success(message);
      }
    },
    
    /**
     * Actualizar filtros
     */
    updateFilters(newFilters: Partial<TicketsState['filters']>): void {
      Object.assign(state.filters, newFilters);
    },
    
    /**
     * Limpiar filtros
     */
    clearFilters(): void {
      state.filters = {
        status: [],
        priority: [],
        assignedTo: [],
        search: '',
        escalated: null,
        favorites: false
      };
    },
    
    /**
     * Actualizar ordenamiento
     */
    updateSorting(sortBy: TicketsState['sortBy'], sortOrder: TicketsState['sortOrder']): void {
      state.sortBy = sortBy;
      state.sortOrder = sortOrder;
    },
    
    /**
     * Forzar recarga de tickets (CON notificaciones por ser método manual)
     */
    async refresh(): Promise<void> {
      notifications.info('Actualizando tickets...');
      await this.loadTickets(undefined, { showNotifications: true });
    },
    
    /**
     * Limpiar el store
     */
    clear(): void {
      state.tickets.clear();
      state.error = null;
      state.lastSync = null;
    },
    
    /**
     * Desconectar y limpiar (modo webhook sin WebSocket)
     */
    destroy(): void {
      // DESACTIVADO: No desconectar WebSocket ya que no está activo
      // webSocket.disconnect();
      this.clear();
    }
  };
  
  /*
   * DESACTIVADO: Configurar listeners de WebSocket para actualizaciones en tiempo real
   * Esta función se mantiene comentada para futuras implementaciones cuando WebSocket esté disponible
   */
  /*
  function setupWebSocketListeners(): void {
    // Listener para tickets actualizados
    webSocket.onTicketUpdated((ticketData: Ticket) => {
      actions.addOrUpdateTicket(ticketData);
      notifications.info(`Ticket #${ticketData.id} actualizado`, {
        timeout: 3000
      });
    });
    
    // Listener para tickets nuevos
    webSocket.onTicketCreated((ticketData: Ticket) => {
      actions.addOrUpdateTicket(ticketData);
      notifications.success(`Nuevo ticket creado: #${ticketData.id}`, {
        timeout: 5000
      });
    });
    
    // Listener para tickets eliminados
    webSocket.onTicketDeleted((ticketId: string) => {
      actions.removeTicket(ticketId);
      notifications.warning(`Ticket #${ticketId} eliminado`);
    });
    
    // Listener para cambios de estado específicos
    webSocket.onStatusChanged((data: { ticketId: string, oldStatus: string, newStatus: string }) => {
      actions.updateTicketStatus(data.ticketId, data.newStatus);
    });
    
    // Listener para errores
    webSocket.onError((error: any) => {
      state.error = error.error || 'Error de WebSocket';
    });
  }
  */
  
  // Getters computados adicionales
  const getters = {
    /**
     * Obtener tickets por prioridad
     */
    getTicketsByPriority: (priority: string) => {
      return computed(() => 
        tickets.value.filter(ticket => ticket.priority === priority)
      );
    },
    
    /**
     * Obtener tickets por usuario asignado
     */
    getTicketsByAssignee: (assignedTo: string) => {
      return computed(() => 
        tickets.value.filter(ticket => ticket.assignedTo === assignedTo)
      );
    },
    
    /**
     * Obtener tickets escalados
     */
    getEscalatedTickets: computed(() => 
      tickets.value.filter(ticket => ticket.escalated)
    ),
    
    /**
     * Obtener tickets favoritos
     */
    getFavoriteTickets: computed(() => 
      tickets.value.filter(ticket => ticket.isFavorite)
    ),
    
    /**
     * Obtener tickets activos (no cerrados)
     */
    getActiveTickets: computed(() => {
      return tickets.value.filter(ticket => {
        const statusData = statusService.getStatusData(ticket.status);
        return statusData?.category !== 'cerrado';
      });
    }),
    
    /**
     * Obtener contadores por categoría
     */
    getCategoryCounts: computed(() => {
      const counts: Record<string, number> = {
        no_iniciado: 0,
        activo: 0,
        hecho: 0,
        cerrado: 0
      };
      
      tickets.value.forEach(ticket => {
        const statusData = statusService.getStatusData(ticket.status);
        if (statusData && statusData.category in counts) {
          counts[statusData.category]++;
        }
      });
      
      return counts;
    }),
    
    /**
     * Obtener lista de usuarios asignados únicos
     */
    getUniqueAssignees: computed(() => {
      const assignees = new Set<string>();
      tickets.value.forEach(ticket => {
        if (ticket.assignedTo && ticket.assignedTo !== 'Sin asignar') {
          assignees.add(ticket.assignedTo);
        }
      });
      return Array.from(assignees).sort();
    }),
    
    /**
     * Verificar si hay tickets con actividad reciente
     */
    hasRecentActivity: computed(() => {
      const now = new Date();
      return tickets.value.some(ticket => {
        if (!ticket.lastActivity) return false;
        const lastActivity = new Date(ticket.lastActivity);
        const diffInHours = (now.getTime() - lastActivity.getTime()) / (1000 * 60 * 60);
        return diffInHours <= 24;
      });
    })
  };
  
  return {
    // Estado reactivo
    state: readonly(state),
    
    // Computed properties
    tickets: readonly(tickets),
    getAllTickets: tickets, // Alias directo para compatibilidad con Dashboard
    filteredTickets: readonly(filteredTickets),
    ticketsByStatus: readonly(ticketsByStatus),
    statusStatistics: readonly(statusStatistics),
    
    // Getters
    ...getters,
    
    // Acciones
    ...actions,
    
    // Estado de WebSocket (DESACTIVADO - valores mock)
    isWebSocketConnected: computed(() => false), // Siempre false ya que WebSocket está desactivado
    webSocketConnectionState: computed(() => 'disconnected'), // Estado fijo 'disconnected'
    isWebSocketEnabled: computed(() => isWebSocketEnabled),
    
    // DESACTIVADO: Métodos de WebSocket
    // connectWebSocket: webSocket.connect,
    // disconnectWebSocket: webSocket.disconnect,
    
    // Utilidades
    isLoading: computed(() => state.loading),
    hasError: computed(() => state.error !== null),
    isEmpty: computed(() => state.tickets.size === 0),
    totalCount: computed(() => state.tickets.size),
    filteredCount: computed(() => filteredTickets.value.length)
  };
}

/**
 * Instancia global del store para uso en toda la aplicación
 */
let globalTicketsStore: ReturnType<typeof useTicketsStore> | null = null;

/**
 * Obtener instancia global del store de tickets
 */
export function getGlobalTicketsStore() {
  if (!globalTicketsStore) {
    globalTicketsStore = useTicketsStore();
  }
  return globalTicketsStore;
}

/**
 * Inicializar el store global (llamar desde main.ts)
 */
export async function initializeTicketsStore(): Promise<void> {
  const store = getGlobalTicketsStore();
  await store.initialize();
}

export default useTicketsStore;
